/**
 * Attribute value helpers for Catalog Management
 *
 * Centralizes the logic for extracting and computing effective attribute values,
 * replacing the duplicated getValues() pattern across views and components.
 */
import { ProductAttribute } from '../types';

/**
 * Extract the individual values from a ProductAttribute.
 * Prefers the `values` array; falls back to splitting the `value` string.
 */
export function getValues(attr: ProductAttribute): string[] {
  if (attr.values && attr.values.length > 0) return attr.values;
  if (!attr.value || attr.value === '-') return [];
  return attr.value.split(', ').map((v) => v.trim()).filter(Boolean);
}

/**
 * The full ordered list of value labels the renderer paints as chips for an
 * imported attribute in edit mode. Mirrors the inline construction in
 * `ExpandedRowContent::renderEditBadge`'s parent block:
 *
 *   const allValues = [...originalValues, ...addedValues];
 *
 * where `addedValues` is everything in `editedValues` that wasn't already in
 * the original `values` array (i.e. user added it this session via the
 * inline `+` or the "Add More Values" picker).
 *
 * Use this when you need to look up a chip BY ITS RENDER INDEX (the `vi`
 * passed up through `onDeleteAttributeValue` / `onRevertAttributeValue`
 * etc.). The bare `getValues(attr)` returns originals only, so an index
 * past `originalValues.length` resolves to `undefined` and downstream
 * `filter((v) => v !== undefined)` is a no-op — making the × on newly-
 * added chips silently dead.
 */
export function getMergedValues(attr: ProductAttribute): string[] {
  const originals = getValues(attr);
  if (attr.editedValues === undefined) return originals;
  const originalsSet = new Set(originals);
  const addedValues = attr.editedValues.filter((v) => !originalsSet.has(v));
  return [...originals, ...addedValues];
}

/**
 * Compute the effective values for an attribute — the values that should be
 * sent to the backend if the user makes no edits.
 *
 * This means:
 * - Keep imported values whose status is NOT 'removed'
 * - Add catalog-only values (values in catalogAttr but not in the imported set)
 */
export function getEffectiveValues(
  attr: ProductAttribute,
  catalogAttr?: ProductAttribute
): string[] {
  const values = getValues(attr);
  const statuses = attr.valueStatuses || [];
  // Keep non-removed imported values
  const result = values.filter((_, i) => (statuses[i] || 'unchanged') !== 'removed');
  // Add catalog-only values
  if (catalogAttr) {
    const catVals = getValues(catalogAttr);
    const importedSet = new Set(values);
    catVals.forEach((v) => {
      if (!importedSet.has(v)) result.push(v);
    });
  }
  return result;
}
